import { FormInputProps } from 'components/Form/next/props';
import { SpaceGroup } from 'components/SpaceBooking/types';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { useVirtualKeyboard } from 'hooks/useVirtualKeyboard';
import Joi from 'joi';
import { initial, last, pick } from 'lodash';
import { useRef, useState } from 'react';
import { Typeahead } from 'react-bootstrap-typeahead';
import { useController, useFormContext } from 'react-hook-form';
import useSWR from 'swr';
import { joiResolver } from 'validation/joiResolver';
import { Button, StyledInput } from '~/components/ui';

interface Fellow {
    name: string;
    email: string;
}

interface Props extends FormInputProps {
    spaceGroup: SpaceGroup;
    email?: string;
}

export const FellowsForm = ({ spaceGroup, name, email }: Props) => {
    const { control } = useFormContext();

    const {
        field: { onChange, onBlur, value }
    } = useController({
        name,
        control,
        defaultValue: []
    });
    const allowFellows = spaceGroup?.allow_fellows || false;
    const fellowEmailOptional = spaceGroup?.fellow_email_optional || false;
    const [addGuestFellow, setAddGuestFellow] = useState<Fellow | null>(null);
    const [guestFellowValidation, setGuestFellowValidation] = useState(null);
    const debouncedUserEmail = useDebouncedValue(email, 800);
    const { data: clubMemberData, error: clubMemberError } = useSWR<{ data?: Fellow[] }>(
        allowFellows ? [`space/group/${spaceGroup.id}/members`, { email: debouncedUserEmail }] : null
    );
    const isLoading = !clubMemberData && !clubMemberError;
    const typeaheadRef = useRef(null);
    useVirtualKeyboard(typeaheadRef.current);

    const handleChange = (_options: any) => {
        const options = _options.map((option) => pick(option, ['name', 'email', 'customOption']));
        const newItem: any = last(options);

        if (options.length > value?.length && newItem?.customOption) {
            setAddGuestFellow(newItem);
            onChange(initial(options));
        } else {
            onChange(options);
        }
    };

    const addFellow = () => {
        joiResolver(
            Joi.object({
                name: Joi.string().required().label('Name des Mitspielers'),
                email: Joi.string()
                    .email({ tlds: false })
                    .when('fellowEmailOptional', {
                        is: true,
                        then: Joi.allow('', null),
                        otherwise: Joi.required()
                    })
                    .label('E-Mail des Mitspielers')
            }).unknown()
        )({ ...addGuestFellow, fellowEmailOptional }).then((data) => {
            setGuestFellowValidation(data.errors);

            if (Object.keys(data.errors).length <= 0) {
                onChange([...value, { ...addGuestFellow, name: addGuestFellow.name }]);
                setAddGuestFellow(null);
                setGuestFellowValidation(null);
            }
        });
    };

    const cancelAddFellow = () => {
        setAddGuestFellow(null);
        setGuestFellowValidation(null);
    };

    return (
        <>
            {allowFellows && (
                <>
                    <hr />
                    <label>Mitspieler</label>
                    <Typeahead
                        isLoading={isLoading}
                        emptyLabel="Keine Übereinstimmungen gefunden"
                        id="fellows_input"
                        options={clubMemberData?.data || []}
                        selected={value}
                        newSelectionPrefix="Mitspieler eintragen als: "
                        labelKey="name"
                        onChange={handleChange}
                        onBlur={onBlur}
                        multiple
                        allowNew
                        ref={typeaheadRef}
                    />

                    <div
                        className="tw-container"
                        style={{ height: addGuestFellow !== null ? 'auto' : '0', overflow: 'hidden' }}
                    >
                        <StyledInput
                            label="Name des Mitspielers"
                            name="guest_fellow_name"
                            isInvalid={guestFellowValidation?.name}
                            value={addGuestFellow?.name || ''}
                            onChange={(e) => setAddGuestFellow({ ...addGuestFellow, name: e.target.value })}
                            helperText={guestFellowValidation?.name?.message}
                        />

                        <StyledInput
                            label="E-Mail Adresse des Mitspielers"
                            name="guest_fellow_email"
                            className={'form-control' + (guestFellowValidation?.email ? ' is-invalid' : '')}
                            value={addGuestFellow?.email || ''}
                            onChange={(e) => setAddGuestFellow({ ...addGuestFellow, email: e.target.value })}
                            helperText={guestFellowValidation?.email?.message}
                        />
                        <div className="tw-flex tw-w-full tw-items-center tw-gap-2">
                            <Button color="success" type="button" onClick={addFellow}>
                                Gast eintragen
                            </Button>
                            <Button color="error" type="button" onClick={cancelAddFellow}>
                                Abbrechen
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};
